import * as React from "react"

type Props = {
  /** How early to start loading before it appears */
  rootMargin?: string
  /** Placeholder shown before it loads */
  fallback?: React.ReactNode
  /** If you want a fixed placeholder height to avoid layout shift */
  minHeight?: number | string
  children: React.ReactNode
  className?: string
}

export default function LazySection({
  rootMargin = "600px 0px", // start loading ~600px before entering view
  fallback,
  minHeight,
  children,
  className,
}: Props) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [show, setShow] = React.useState(false)

  React.useEffect(() => {
    if (show) return
    const el = ref.current
    if (!el) return

    // If IntersectionObserver not supported, just render
    if (typeof IntersectionObserver === "undefined") {
      setShow(true)
      return
    }

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          setShow(true)
          io.disconnect()
        }
      },
      { root: null, rootMargin, threshold: 0.01 }
    )

    io.observe(el)
    return () => io.disconnect()
  }, [show, rootMargin])

  return (
    <div
      ref={ref}
      className={className}
      style={minHeight != null && !show ? { minHeight } : undefined}
    >
      {show ? children : fallback ?? null}
    </div>
  )
}